import React from 'react'
// import { useTranslation } from 'react-i18next';
import { useParams, Redirect } from 'react-router-dom'
import TopNavigation from '../../TopNavigation'
import { Container } from 'reactstrap'
import { useSelector } from 'react-redux'
import { Button, Chip, CircularProgress, Divider, Grid, Typography } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import withAppconfig from '../../AppContext'
import { getLastvalidDate } from './MembershipUtil'
import { isContinuousProduct, periodLabel } from '../../utils/subscriptions'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { firebaseConnect, firestoreConnect, isLoaded } from 'react-redux-firebase'
import { Alert } from 'reactstrap'
import { cardList, setAddCardReturn } from '../../utils/storedcards'
import { useAddCard } from '../../utils/addcard'
import ProfileInput from '../../components/ProfileInput'
import Poster from '../../components/Poster'

const enhance = compose(
  connect((state) => ({ auth: state.firebase.auth })),
  // The shop's memberships - the same query as the list (MembershipsScreen), so this page also works
  // when it is the first page loaded.
  firebaseConnect((props) => [
    {
      path: 'shop/' + props.appconfig.accountkey + '/memberships',
      queryParams: ['orderByChild=displayName'],
      storeAs: 'shopmemberships',
    },
  ]),
  // Stored cards (masked): a continuous membership can only be bought with one, so the page says so
  // before the customer gets to checkout. Same query as the profile and the checkout.
  firestoreConnect((props) => [
    { collection: 'users', doc: `${props.auth.uid}`, subcollections: [{ collection: 'cards' }], storeAs: 'cards' },
  ]),
  connect(({ globaltime: { gtime }, firestore }) => ({
    gtime,
    cards: firestore.data['cards'],
  }))
)

function MembershipScreen({ appconfig, gtime, cards }) {
  const params = useParams()
  const dispatch = useDispatch()
  const history = useHistory()
  const { t } = useTranslation()
  // Straight to Paytrail's card form - no /addcard page in between (utils/addcard.js).
  const addCardFlow = useAddCard({ appconfig })
  const [selectedVariant, setSelectedVariant] = React.useState(null)
  const [profileIsValid, setProfileIsValid] = React.useState(false)
  const shopmemberships = useSelector((state) => state.firebase.data.shopmemberships)
  const membership = shopmemberships ? shopmemberships[params.id] : undefined

  // This page loads the shop itself (enhance below): it is opened directly too, not only from the
  // list - e.g. coming back from Paytrail's card form, a full page load. Wait for the shop, and only
  // a product that really is not in it goes back to the list. (It used to navigate during render
  // and return nothing, which left a white page.)
  if (!isLoaded(shopmemberships)) {
    return (
      <div className='full-page-background'>
        <TopNavigation />
        <Container className='front' style={{ paddingTop: 40, textAlign: 'center' }}>
          <CircularProgress />
        </Container>
      </div>
    )
  }
  if (!membership) return <Redirect to={appconfig.pathPrefix + '/shop/memberships'} />
  const { poster } = membership
  let variationsSelector = null
  if (membership.variations) {
    if (membership.variations.length > 1) {
      variationsSelector = membership.variations.map((v) => {
        return (
          <Chip
            clickable
            label={v.name}
            key={'varid-' + v.varid}
            color={selectedVariant?.varid === v.varid ? 'primary' : 'default'}
            variant={selectedVariant?.varid === v.varid ? 'outlined' : 'default'}
            onClick={() => setSelectedVariant(v)}
            style={{ margin: 5 }}
          />
        )
      })
    } else {
      variationsSelector = <Typography variant='h5'>{membership.variations[0].name}</Typography>
      if (selectedVariant === null) {
        setSelectedVariant(membership.variations[0])
      }
    }
  }

  const goToPay = (membership) => {
      dispatch({
        type: 'UPDATE_PAYABLE_MEMBERSHIP',
        payload: membership,
      })
      history.push(appconfig.pathPrefix + '/shop/checkout')
  }

  // A continuous membership renews every period until cancelled, so its first period's end date is
  // not "how long it lasts" - say what it costs per period instead.
  const continuous = isContinuousProduct(membership)
  const period = continuous ? periodLabel(membership.validity, t) : ''
  // A continuous membership is paid with a stored card that has not expired. Until the cards have
  // loaded nothing is said either way, so the note does not flash for a customer who has one.
  const cardsLoaded = isLoaded(cards)
  const hasUsableCard = cardList(cards).some((c) => !c.expired)
  const needsCard = continuous && cardsLoaded && !hasUsableCard
  const subscriptionBlocked = continuous && (!appconfig.storedcards || !cardsLoaded || !hasUsableCard)
  const addCard = () => {
    // Paytrail's card form reloads the app; come back to this product afterwards.
    setAddCardReturn(appconfig.pathPrefix + '/shop/membership/' + membership.id)
    addCardFlow.start()
  }
  let lastvalidDate = getLastvalidDate(membership.validity, gtime)
  if (continuous) {
    lastvalidDate = t('subscription_product_note')
  } else if (lastvalidDate === 'permanent') {
    lastvalidDate = t('membership_permanent')
  } else {
    lastvalidDate = t('valid') + ' ' + lastvalidDate.format('D.M.YYYY')
  }

  return (
    <div className='full-page-background'>
      <TopNavigation />
      <Container className='front' style={{ paddingTop: 10 }}>
        <Grid container spacing={3} alignItems='flex-start' >
          <Grid item xs={12} md={6} style={{ textAlign: 'center', paddingTop: 40 }}>
            <Poster data={poster} preferredSize={300} alt={'Product image'} />
          </Grid>
          <Grid item xs={12} md={6} style={{ paddingTop: '5em', paddingRight: 20 }}>
            <ProfileInput onValid={(isValid) => setProfileIsValid(isValid)} label={'Jotta voit ostaa jäsenyyden täydennä seuraavat tiedot'} />
            <Typography variant='h4'>{membership.displayName.toUpperCase()}</Typography>
            <Typography variant='button'>{lastvalidDate}</Typography>
            <Divider />
            {variationsSelector}
            <div>
              <Typography variant='h6'>
                {selectedVariant ? (selectedVariant.price / 100).toFixed(2).replace('.', ',') + ' €' + (period ? ' / ' + period : '') : null}
              </Typography>
              <Typography variant='caption'>
                {selectedVariant ? t('vat') + ' ' + membership.vat + ' %' : null}
              </Typography>
            </div>

            {continuous && !appconfig.storedcards ? (
              <Alert color='secondary' style={{ marginTop: 10 }}>{t('subscription_unavailable')}</Alert>
            ) : needsCard ? (
              <Alert color='info' style={{ marginTop: 10 }}>
                {t('subscription_paid_with_card')}{' '}
                <Button size='small' color='primary' disabled={addCardFlow.starting} onClick={addCard}>
                  {addCardFlow.starting ? <CircularProgress size={16} /> : t('add_card')}
                </Button>
                <div style={{ fontSize: 'small', marginTop: 5 }}>{t('card_storage_note')}</div>
                {addCardFlow.failed ? (
                  <div style={{ color: '#b00020', marginTop: 5 }}>{t('card_add_failed_init')}</div>
                ) : null}
              </Alert>
            ) : null}
            {continuous && appconfig.storedcards && appconfig.subscriptionsaldofirst ? (
              <div style={{ fontSize: 'small', marginTop: 10 }}>{t('subscription_saldo_note')}</div>
            ) : null}
            <Button
              variant='contained'
              color='primary'
              disabled={!selectedVariant || !profileIsValid || subscriptionBlocked}
              style={{ marginTop: 10, marginBottom: 10 }}
              onClick={() => goToPay({ ...membership, selectedVariant })}
            >
              {t('buy_membership')}
            </Button>
            <Divider />
            <Typography variant='body1'>
              <span dangerouslySetInnerHTML={{ __html: membership.description }} />
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </div>
  )
}

export default withAppconfig(enhance(MembershipScreen))
